import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Wifi, Clock, HardDrive, CreditCard, Settings, X, Search, Filter } from 'lucide-react';
import PaginationControls from '../../components/admin/PaginationControls';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceFilter, setPriceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [formData, setFormData] = useState({
    name: '',
    up_mbps: '',
    down_mbps: '',
    validity_seconds: '86400',
    bytes_quota: '',
    price: '0',
  });

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, priceFilter, pageSize]);

  const loadPlans = async () => {
    try {
      const response = await adminAPI.getPlans();
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error(error.response?.data?.error || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => {
      const matchesSearch = !searchTerm || [
        plan.name,
        `${plan.up_mbps}`,
        `${plan.down_mbps}`,
        `${plan.price ?? 0}`,
      ].some((value) => String(value || '').toLowerCase().includes(searchTerm.toLowerCase()));

      const price = Number(plan.price || 0);
      const matchesPrice =
        priceFilter === 'all' ||
        (priceFilter === 'free' && price === 0) ||
        (priceFilter === 'paid' && price > 0);

      return matchesSearch && matchesPrice;
    });
  }, [plans, searchTerm, priceFilter]);

  const paginatedPlans = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPlans.slice(start, start + pageSize);
  }, [filteredPlans, page, pageSize]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const planData = {
      ...formData,
      up_mbps: parseInt(formData.up_mbps, 10),
      down_mbps: parseInt(formData.down_mbps, 10),
      validity_seconds: parseInt(formData.validity_seconds, 10),
      bytes_quota: formData.bytes_quota ? parseInt(formData.bytes_quota, 10) : null,
      price: parseFloat(formData.price) || 0,
    };

    try {
      if (editingPlan) {
        await adminAPI.updatePlan(editingPlan.id, planData);
        toast.success('Plan updated successfully');
      } else {
        await adminAPI.createPlan(planData);
        toast.success('Plan created successfully');
      }

      setShowModal(false);
      setEditingPlan(null);
      resetForm();
      loadPlans();
    } catch (error) {
      console.error('Failed to save plan:', error);
      toast.error(error.response?.data?.error || 'Failed to save plan');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      up_mbps: '',
      down_mbps: '',
      validity_seconds: '86400',
      bytes_quota: '',
      price: '0',
    });
  };

  const deletePlan = async (planId, planName) => {
    if (!window.confirm(`Are you sure you want to delete plan "${planName}"?`)) {
      return;
    }

    try {
      const response = await adminAPI.deletePlan(planId);
      toast.success(response.data?.message || `Plan "${planName}" deleted successfully`);
      loadPlans();
    } catch (error) {
      console.error('Failed to delete plan:', error);
      toast.error(error.response?.data?.error || `Failed to delete plan "${planName}"`);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${seconds / 60} minute${seconds > 60 ? 's' : ''}`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'Unlimited';
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(1)} GB`;
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      up_mbps: plan.up_mbps.toString(),
      down_mbps: plan.down_mbps.toString(),
      validity_seconds: plan.validity_seconds.toString(),
      bytes_quota: plan.bytes_quota?.toString() || '',
      price: plan.price?.toString() || '0',
    });
    setShowModal(true);
  };

  return (
    <div className="p-8">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <CreditCard className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">VPN Plans</h1>
              <p className="text-sm text-gray-600 mt-1">
                Create and maintain bandwidth, duration, quota, and price configurations in a cleaner responsive view.
              </p>
            </div>
          </div>

          <button type="button" onClick={openCreateModal} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800">
            <Plus className="h-4 w-4" />
            <span>Create Plan</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search plan name, speed, or price"
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <select value={priceFilter} onChange={(event) => setPriceFilter(event.target.value)} className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
              <option value="all">All Plans</option>
              <option value="free">Free Only</option>
              <option value="paid">Paid Only</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 mt-4">Loading plans...</p>
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <CreditCard className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{plans.length === 0 ? 'No plans created yet' : 'No plans match your filters'}</h3>
          <p className="text-gray-600 mb-6">
            {plans.length === 0
              ? 'Create your first VPN plan to define bandwidth, duration, quota, and price in the admin panel.'
              : 'Adjust the search or filter settings to see more plans.'}
          </p>
          {plans.length === 0 ? (
            <button type="button" onClick={openCreateModal} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800">
              <Plus className="h-4 w-4" />
              <span>Create First Plan</span>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {paginatedPlans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Settings className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate text-xl font-semibold text-gray-900">{plan.name}</h2>
                      <p className="text-sm text-gray-600">Plan definition</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => openEditModal(plan)} className="p-1.5 rounded hover:bg-gray-50 text-gray-400 hover:text-gray-600" title="Edit Plan">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deletePlan(plan.id, plan.name)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600"
                      title="Delete Plan"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-md">
                          <Wifi className="h-4 w-4 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-600">Speed</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {plan.up_mbps} / {plan.down_mbps} Mbps
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-md">
                          <Clock className="h-4 w-4 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-600">Duration</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{formatDuration(plan.validity_seconds)}</span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-md">
                          <HardDrive className="h-4 w-4 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-600">Data quota</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{formatBytes(plan.bytes_quota)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg bg-white border border-gray-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">Price</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    ${Number(plan.price || 0).toFixed(2)}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">{Number(plan.price || 0) > 0 ? 'Paid access' : 'Free access'}</p>
                </div>
              </div>
            ))}
          </div>

          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalItems={filteredPlans.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="plans"
          />
        </>
      )}

      {showModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {editingPlan ? 'Edit VPN Plan' : 'Create VPN Plan'}
                    </h2>
                    <p className="text-sm text-gray-500">Visual refresh only. Plan save and update logic is unchanged.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPlan(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    placeholder="e.g. 10M Daily Plan"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload Speed (Mbps)</label>
                    <input
                      type="number"
                      value={formData.up_mbps}
                      onChange={(e) => setFormData({ ...formData, up_mbps: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                      placeholder="10"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Download Speed (Mbps)</label>
                    <input
                      type="number"
                      value={formData.down_mbps}
                      onChange={(e) => setFormData({ ...formData, down_mbps: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                      placeholder="10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Duration</label>
                  <select
                    value={formData.validity_seconds}
                    onChange={(e) => setFormData({ ...formData, validity_seconds: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  >
                    <option value="3600">1 Hour</option>
                    <option value="21600">6 Hours</option>
                    <option value="43200">12 Hours</option>
                    <option value="86400">24 Hours</option>
                    <option value="604800">7 Days</option>
                    <option value="2592000">30 Days</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Quota (GB, optional)</label>
                    <input
                      type="number"
                      value={formData.bytes_quota ? (parseInt(formData.bytes_quota, 10) / (1024 * 1024 * 1024)).toString() : ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bytes_quota: e.target.value
                            ? (parseFloat(e.target.value) * 1024 * 1024 * 1024).toString()
                            : '',
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                      placeholder="Leave empty for unlimited"
                      step="0.1"
                    />
                    <p className="text-sm text-gray-600 mt-1">Enter in GB, for example 1 for 1GB.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                      placeholder="0.00"
                    />
                    <p className="text-sm text-gray-600 mt-1">Set to 0 for free access.</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-4 sm:flex-row">
                  <button type="submit" className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition flex items-center justify-center">
                    <CreditCard className="h-4 w-4 mr-2" />
                    <span>{editingPlan ? 'Update Plan' : 'Create Plan'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPlan(null);
                      resetForm();
                    }}
                  className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Plans;
